import 'polyfills';

import React, { FunctionComponent, useEffect } from 'react';

import FullPageWrapper from 'scenes/common/components/FullPageWrapper/FullPageWrapper';
import { Heading, Text, List, ListItem } from 'components/chroma';
import { Wrapper } from 'scenes/outage/components/ScheduledMaintenance';
import { v4 as uuid } from 'uuid';
import styled from 'styled-components';
import {
  colesInsuranceMailToId,
  privacyPolicy,
} from 'scenes/common/helpers/contacts';

const CircleListItem = styled.li`
  list-style-type: circle !important;
`;

const PageLink = styled.a`
  text-decoration: none;
`;

const TermsAndConditions: FunctionComponent = () => {
  useEffect(() => {
    document.title = `terms and conditions | Coles Insurance`;
  }, []);

  return (
    <FullPageWrapper
      shouldSkipHardcodedHeadingColor
      onClickCallUs={() => {}}
      isLoading={false}
    >
      <Wrapper>
        <Heading size={2} color="primary">
          Website terms and conditions
        </Heading>
        <Text>
          This website is owned and operated by Insurance Australia Limited
          under the domain name coles.com.au/insurance (Website). References in
          this page to “you” and “your” is a reference to any person accessing
          or using this Website or any part of this Website.
        </Text>
        <Text>
          Your access and use of this Website is conditional upon your
          acceptance of, and compliance with, these Terms and Conditions of Use,
          the Privacy Statement, any statements, disclaimers or notices
          contained in this Website, and other terms and conditions set out in
          this Website including terms and conditions relating to products and
          services offered on the Website (Terms).
        </Text>
        <Text>
          By accessing the Website, you are deemed to have accepted and agreed
          to the Terms.
        </Text>
        <Text>
          Insurance Australia Limited may, at any time and without notification
          to you:
        </Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              vary the Terms on this Website from time to time and any varied
              Terms will be made available on this Website. By your use of this
              Website after any variation, you are taken to have accepted the
              varied Terms;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>alter the content or functionality of this Website; or</Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              remove or halt access to this Website or any component or function
              on this Website.
            </Text>
          </ListItem>
        </List>
        <br />
        <Heading size={4}>Electronic Documents</Heading>
        <Text>
          By purchasing an insurance policy through this Website, you agree that
          Insurance Australia Limited may provide, and you will receive or
          access, your insurance documents electronically.
        </Text>
        <Text>You further agree to:</Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              Inform of us of any changes to your preferred email address to
              receive notifications and electronic documents
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              Keep the email account where you have chosen to receive electronic
              documents from Insurance Australia Limited active and capable of
              receiving new emails
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              To take responsibility for your chosen email provider blocking or
              marking spam emails containing electronic documents sent by
              Insurance Australia Limited
            </Text>
          </ListItem>
        </List>
        <br />
        <Text>
          The following terms and conditions apply to your receipt, access and
          use of electronic insurance documents:
        </Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              You will receive or access all of your insurance documents
              electronically, when permitted by law. There may be some documents
              that Insurance Australia Limited either chooses to not provide
              electronically or cannot deliver electronically due to legal and
              technological constraints. These documents will be sent to the
              postal address appearing in our records for you, via Australia
              Post.
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              You will need a valid email account and Internet access to receive
              your insurance documents, and Adobe Acrobat Reader version 5.0 or
              greater to view and print your electronic insurance documents.
              Should we make changes to these hardware or software requirements
              that may result in your no longer being able to access or receive
              your documents, we will inform you at your chosen email address.
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              Each notification or receipt of an electronic insurance document
              will be deemed to be received by you at the time it leaves our
              information system.
            </Text>
          </ListItem>
        </List>
        <br />
        <Heading size={4}>Disclaimer and Limit of Liability</Heading>
        <Text>
          Insurance Australia Limited excludes the operation of all terms
          implied by law, except for those which cannot be lawfully excluded.
        </Text>
        <Text>To the extent permitted by law:</Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              Insurance Australia Limited makes no representations about the
              suitability of the content of this Website for any purpose. All
              content is provided &quot;as is&quot; without any warranty of any
              kind.
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              Insurance Australia Limited does not make any representations or
              warranties
            </Text>
            <List margin={{ left: 6 }}>
              <CircleListItem key={uuid()}>
                <Text>
                  to the effect that this Website is fault free or will operate
                  free from viruses;
                </Text>
              </CircleListItem>
              <CircleListItem key={uuid()}>
                <Text>
                  regarding the continual accessibility or reliability of this
                  Website at any time
                </Text>
              </CircleListItem>
              <CircleListItem key={uuid()}>
                <Text>
                  as to the appropriateness of the Website for your needs.
                </Text>
              </CircleListItem>
            </List>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              Insurance Australia Limited excludes any warranties and conditions
              implied by law (with the exception of that which cannot be
              lawfully excluded) including warranties and conditions regarding
              merchantability, fitness for a particular purpose, title and
              intellectual property non-infringement.
            </Text>
          </ListItem>
          <br />
        </List>
        <Text>
          With the exception of obligations implied by law which cannot be
          excluded, you agree that in no event will Insurance Australia Limited,
          its directors, employees, contractors, agents or related bodies
          corporate be liable for any loss arising from or in connection with
          the material on this Website, or use of or access to (or inability to
          use or access or any delay in using or accessing) this Website.
        </Text>
        <Text>
          Without limiting the foregoing provision notwithstanding any other
          provision of the Terms, with the exception of obligations implied by
          law which cannot be excluded, you agree that in no event shall
          Insurance Australia Limited or any related body corporate (as that
          term is defined in the Corporations Act 2001), its directors,
          employees, agents and contractors, be liable to you in contract, in
          tort (including but not limited to negligence), in equity, by
          operation of statute or otherwise for any kind of:
        </Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              special, indirect, exemplary or consequential loss or damages
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>remote or unforeseeable loss;</Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>loss of opportunity;</Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>loss of revenue;</Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>loss of profit or anticipated profit;</Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>loss of contracts;</Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>loss of goodwill; or</Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>loss arising from business interruption.</Text>
          </ListItem>
        </List>
        <br />
        <Text>
          arising out of or in connection with the use, copying or display of
          the content on this Website, the inability to use or access this
          Website or any delay in being able to use or access this Website.
        </Text>
        <Text>
          Although Insurance Australia Limited has taken all reasonable steps to
          ensure that the Website content is error free at the time of its
          publication, to the extent permitted by law (including laws implying
          terms which cannot be excluded), Insurance Australia Limited makes no
          warranty as to the accuracy, completeness or currency of the content
          on this Website. It is your responsibility to verify any information
          before relying on it. The content of this Website may include
          technical or typographical errors. From time to time changes are made
          to the content herein. Insurance Australia Limited may make changes in
          the products and/or the services described herein at any time.
        </Text>
        <Heading size={4}>Links to Third Party Website</Heading>
        <Text>
          For your reference and convenience, this Website may contain links to
          websites which are owned by third parties. Insurance Australia Limited
          cannot control third party websites, and therefore is not responsible
          for (and makes no representation or warranty regarding) the content of
          any information on these linked sites. You are free to access these
          other sites, but you do so at your own risk.
        </Text>
        <Text>
          Prior to disclosing your personal details on any linked site,
          Insurance Australia Limited recommends that you peruse their Terms and
          Privacy Statement as they may differ from the Terms and Privacy
          Statement included on this Website.
        </Text>
        <Text>
          In addition, Insurance Australia Limited is not liable for the content
          on third party websites or any loss or damage you may incur through
          your use of these websites.
        </Text>
        <Heading size={4}>Privacy</Heading>
        <Text>
          Any collection, use or disclosure of your personal information through
          the use of this Website, or otherwise, is subject to Insurance
          Australia Limited’s{' '}
          <PageLink
            href={privacyPolicy()}
            style={{ textDecoration: 'none' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Statement.
          </PageLink>
        </Text>
        <Heading size={4}>Warranties</Heading>
        <Text>
          Insurance Australia Limited does not guarantee that this Website will
          be free from viruses, or that access to this Website will function as
          intended or be uninterrupted. As electronic services are subject to
          interruption or breakdown, access to this Website is offered on an “as
          available” basis only. All terms implied by law, except those that
          cannot be lawfully excluded, are excluded.
        </Text>
        <Heading size={4}>No Unlawful or Prohibited Use/Activities</Heading>
        <Text>
          As a condition of your use of this Website, you warrant that you will
          not use this Website:
        </Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              for any purpose that is unlawful or prohibited by these Terms;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              in any manner which could damage, disable, overburden or impair
              the Website or disrupt or interfere with any other person’s use
              and enjoyment of the Website;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              to knowingly transmit any virus or other disabling feature to or
              via this Website;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              to obtain or attempt to obtain any material or information through
              any means not intentionally made available or provided for through
              this Website;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              to copy, reproduce, alter, modify, create derivative works, or
              publicly display any part of any content from this Website without
              our prior written permission;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              to accumulate or combine the content, data or information
              contained or accessible through this Website with content, data or
              information from any third party, without Insurance Australia
              Limited&apos;s prior written permission;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              to combine or process any data or information from this Website
              with other data or information other than as specifically
              permitted by these Terms;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              for use of high-volume, automated or systematic processes to
              obtain data or other information from this Website;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              to incorporate into or store any information or data from this
              Website in any other website, electronic retrieval system,
              database, publication or other work in any form, other than as
              specifically permitted by these Terms;
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              to create a link to this Website from another website without
              Insurance Australia Limited&apos;s prior written permission.
            </Text>
          </ListItem>
        </List>
        <br />
        <Text>
          You must take all necessary measures to stop the introduction or
          spread of any viruses or any other form of software contamination,
          into this Website when accessing information through this Website.
        </Text>
        <Text>
          This Website may be used only for the purpose of legitimate insurance
          shopping, as defined below, and not for any other purpose.
        </Text>
        <Text>&quot;Legitimate insurance shopping&quot; means:</Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              the process of securing a quote for insurance which you are
              genuinely considering purchasing for yourself or someone else
              (with their consent); and
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>the process of purchasing such insurance.</Text>
          </ListItem>
        </List>
        <Text>
          Prohibited usage includes, but is not limited to, commercial research
          by our competitors or third parties.
        </Text>
        <Text>
          This Website may be used only by human beings, and not by any
          automated system or device whose purpose is to extract information for
          commercial purposes.
        </Text>
        <Text>
          You indemnify Insurance Australia Limited and its directors,
          employees, contractors, agents and related bodies corporate for any
          loss, damage, costs or expenses suffered or incurred arising out of or
          in connection from any breach of these terms or other legal obligation
          by you or arising out of or in connection with your access to this
          Website.
        </Text>
        <Heading size={4}>Australian Residents Only</Heading>
        <Text>
          The insurance products described in this Website are available to
          Australian residents only.
        </Text>
        <Heading size={4}>Copyright</Heading>
        <Text>
          Insurance Australia Limited owns copyright in the information
          contained in this Website. Information may be printed or downloaded
          but only for your personal or informational and non-commercial use
          only. Other than for the purposes of and subject to the conditions
          prescribed under the Copyright Act 1968 (Cth) or other applicable
          laws, the information may not otherwise be reproduced and must not be
          distributed or transmitted to any other person or used in any way
          without the written permission of Insurance Australia Limited.
        </Text>
        <Heading size={4}>No Implied Licenses</Heading>
        <Text>
          Nothing contained herein shall be construed as conferring by
          implication, estoppel or otherwise any license or right under any
          patent, trademark, copyright (except as expressly provided above), or
          proprietary rights of Insurance Australia Limited or any third party.
        </Text>
        <Heading size={4}>No Advice Given</Heading>
        <Text>
          Insurance Australia Limited has prepared the information on this
          Website in good faith but the information does not take into account
          the objectives, financial situation or needs of any Website user and
          the Website does not purport to provide personal financial advice or
          personal investment advice. Accordingly you should consider the
          appropriateness of the information and whether any products or
          services referred to on the Website are appropriate for you, having
          regard to your objectives, financial situation or needs, and seek
          independent professional advice and read the relevant PDS before
          making any decisions regarding such products or services.
        </Text>
        <Heading size={4}>Obtaining a Quote Online</Heading>
        <Text>
          You can obtain a quote for a Coles Car, Home and Landlord Insurance
          policy on this Website by submitting the information requested on our
          online quoting tool. If, based on the information you provide, you are
          eligible to purchase an insurance policy with us according to our
          underwriting guidelines, we will provide you with an online quote.
        </Text>
        <Text>Under these Terms you:</Text>
        <List bullet margin={{ left: 6 }}>
          <ListItem keyProp={uuid()}>
            <Text>
              are not authorised to, and you agree not to, use high-volume,
              automated or systematic processes to obtain quotes or make
              insurance applications, or to make an excessive number of requests
              to the services.
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              you also agree not to use, copy, compile, repackage or disseminate
              any information obtained from the &apos;Get a quote&apos; service
              for any purpose other than your personal, non-commercial purposes
              without Insurance Australia Limited&apos;s prior written consent,
              and you specifically agree that you will not use, or obtain with
              an intention to use, such information for the purpose of
              discovering the business logic of either service, or creating a
              tool that predicts the operation of either service.
            </Text>
          </ListItem>
          <ListItem keyProp={uuid()}>
            <Text>
              You acknowledge that the business logic (including the relative
              importance of each rating criterion and the pricing algorithms)
              used to implement the services are trade secrets of Insurance
              Australia Limited and/or its reinsurers, and that Insurance
              Australia Limited would suffer significant harm if such business
              logic were to be derived, and subsequently used or disclosed, in
              breach of these Terms.
            </Text>
          </ListItem>
        </List>
        <br />
        <Heading size={4}>Buying Insurance Online</Heading>
        <Text>
          To buy an insurance policy via this Website you are required to send
          an electronic application (or offer) to us to purchase an insurance
          policy from us using our online purchasing facility. We consider your
          offer to purchase an insurance policy and advise you of our decision
          whether to accept your offer by notifying you electronically via this
          Website.
        </Text>
        <Text>
          You must pay for any insurance you purchase on this Website with a
          valid credit card accepted by us, by BPAY or bank debit. The credit
          cards we accept are Visa and Mastercard.
        </Text>
        <Text>
          You will not have a binding contract of insurance with us until we
          have been able to successfully charge the quoted premium to your
          nominated credit card or bank account, and we have received the
          premium (where you have selected to pay your premium in one lump sum),
          or when we have received the first instalment (where you have elected
          to pay your premium in instalments).
        </Text>
        <Text>
          We will assume that any electronic instruction received via this
          Website is an authentic instruction. We will not authenticate (nor do
          we have an obligation to authenticate) the identity of any person
          transmitting electronic instructions via this Website. We will not
          verify the accuracy or completeness of electronic instructions.
        </Text>
        <Text>
          Insurance Australia Limited does not guarantee the security of
          transmissions over the internet. Insurance Australia Limited shall not
          be responsible for your access to the internet or for any interception
          or interruption of any communications through the internet.
        </Text>
        <Heading size={4}>General</Heading>
        <Text>
          The Website and these Terms are governed by and construed in
          accordance with the laws in force in the State of New South Wales,
          Australia.
        </Text>
        <Text>
          If any provision in these Terms is found to be unlawful, void or
          unenforceable for any reason, then that provision shall be deemed
          severable from and shall not affect the validity and enforceability of
          the remaining provisions.
        </Text>
        <Text>
          If you breach these Terms, you agree to indemnify us for any loss that
          we may suffer or any costs that may be incurred from your breach.
        </Text>
        <Text>
          Any failure by us to exercise any right, power or remedy under these
          Terms shall not operate as a waiver of our rights.
        </Text>
        <Heading size={4}>Contact</Heading>
        If you have any questions relating to these Terms, please contact us at{' '}
        <PageLink
          href={`mailto:${colesInsuranceMailToId()}`}
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          colesinsurance@iag.com.au
        </PageLink>
      </Wrapper>
    </FullPageWrapper>
  );
};

export default TermsAndConditions;
